import React from 'react';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useIntl, FormattedMessage } from 'react-intl';
import validator from 'validator';
import Box from '@material-ui/core/Box';
import ChevronRight from '@material-ui/icons/ChevronRight';
import { Button, Typography } from '@material-ui/core';
import { showSnackbar } from 'actions';
import { Input } from 'components/FormInputs';
import { useFormik } from 'components/shared/Formik';
import useTextTransformBlur from 'hooks/useTextTransformBlur';
import { toLower } from 'logic/textTransform';

const LoginStepOne = ({ onSubmit, inProgress, error }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const formik = useFormik({
    initialValues: {
      username: '',
    },
    validate: (values) => {
      const errors = {};

      if (!values.username) {
        errors.username = intl.formatMessage({
          id: 'welcome.required',
          defaultMessage: 'This field is required',
        });
      } else if (!validator.isEmail(values.username)) {
        errors.username = intl.formatMessage({
          id: 'welcome.invalidEmail',
          defaultMessage: 'Please fill in a valid email address',
        });
      }

      return errors;
    },
    validateOnBlur: true,
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: (values) => {
      onSubmit({ ...values, username: toLower(values.username) });
    },
  });

  const { handleChange, handleSubmit, values, errors, isValid, setErrors } = formik;

  const handleBlur = useTextTransformBlur(formik, {
    username: toLower,
  });

  React.useEffect(() => {
    if (!isEmpty(error)) {
      setErrors({
        username: intl.formatMessage({
          id: 'welcome.invalidEmail',
          defaultMessage: 'Please fill in a valid email address',
        }),
      });

      if (error.message) {
        dispatch(
          showSnackbar({
            severity: 'error',
            message: error.message,
          }),
        );
      }
    }
  }, [error]);

  return (
    <div>
      <Typography variant="h6">
        <FormattedMessage
          id="welcome.fillEmailHeader"
          defaultMessage="Fill in your e-mail to progress"
        />
      </Typography>
      <form onSubmit={handleSubmit} noValidate>
        <Input
          label={<FormattedMessage id="welcome.fillEmailLabel" defaultMessage="Fill in e-mail" />}
          placeholder={intl.formatMessage({
            id: 'Email',
            defaultMessage: 'Email',
          })}
          name="username"
          type="email"
          InputLabelProps={{
            shrink: true,
          }}
          autoFocus
          errors={errors}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.username}
        />
        <Box mt={2}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disabled={inProgress || !isValid}
            type="submit"
          >
            {inProgress ? (
              <FormattedMessage id="welcome.loading" defaultMessage="Loading" />
            ) : (
              <FormattedMessage id="welcome.continue" defaultMessage="Continue" />
            )}
            <ChevronRight />
          </Button>
        </Box>
      </form>
    </div>
  );
};

LoginStepOne.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  inProgress: PropTypes.bool.isRequired,
  error: PropTypes.shape({
    message: PropTypes.string,
  }),
};

export default LoginStepOne;
